/**
 * Display formatting.
 *
 * Two rules run through this file, and both exist because getting them wrong
 * produces output that looks fine and is wrong:
 *
 *   1. MONEY IS NEVER PARSED INTO A NUMBER. It arrives as a string and is
 *      formatted as a string.
 *   2. TIMESTAMPS ARE UTC INSTANTS, converted to local only at render.
 */

import type { Money, UtcTimestamp } from '@/types/api'

// ------------------------------------------------------------------- money
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
}

/**
 * Format a server amount for display.
 *
 * *** THE STRING IS NEVER CONVERTED TO A NUMBER ***
 * `Number("1234.10")` is 1234.0999999999999 in IEEE 754. Formatting from that
 * can round to a different paisa than the server and the payment gateway agree
 * on — and a reconciliation screen that disagrees with the gateway is worse
 * than no screen.
 *
 * So grouping is applied to the digits themselves, as text.
 */
export function formatMoney(
  amount: Money | null | undefined,
  currency = 'INR',
  options: { showSymbol?: boolean } = {},
): string {
  const { showSymbol = true } = options

  if (amount === null || amount === undefined || amount === '') return '—'

  const symbol = showSymbol ? (CURRENCY_SYMBOLS[currency] ?? `${currency} `) : ''

  const negative = amount.trim().startsWith('-')
  const cleaned = amount.trim().replace(/^-/, '')

  const [whole = '0', fraction = ''] = cleaned.split('.')

  const grouped =
    currency === 'INR' ? groupIndian(whole) : groupThousands(whole)

  // Always two decimals: a column where some rows show ".5" and others ".50"
  // is hard to scan, and money is conventionally written to the minor unit.
  const decimals = (fraction + '00').slice(0, 2)

  return `${negative ? '-' : ''}${symbol}${grouped}.${decimals}`
}

/**
 * Indian grouping: last three digits, then pairs. 1234567 -> 12,34,567.
 *
 * The default currency here is INR and the audience is Indian institutions.
 * Rendering ₹12,34,567 as ₹1,234,567 reads as a different number to someone
 * who groups this way.
 */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits
  const last3 = digits.slice(-3)
  const rest = digits.slice(0, -3)
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** A compact form for dashboard tiles: ₹1.2L, ₹3.4Cr. */
export function formatMoneyCompact(
  amount: Money | null | undefined,
  currency = 'INR',
): string {
  if (!amount) return '—'

  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `
  // `split` can return an empty first element for a malformed value; default
  // rather than index blindly.
  const whole = (amount.split('.')[0] ?? '0').replace('-', '')
  const digits = whole.length
  const negative = amount.startsWith('-')

  // Approximation is acceptable HERE and only here: a headline tile is for
  // orientation. Exact figures appear in the tables, formatted from the string.
  const value = Number(whole)
  const sign = negative ? '-' : ''

  if (currency === 'INR') {
    if (digits > 7) return `${sign}${symbol}${(value / 10_000_000).toFixed(2)}Cr`
    if (digits > 5) return `${sign}${symbol}${(value / 100_000).toFixed(2)}L`
  } else if (digits > 6) {
    return `${sign}${symbol}${(value / 1_000_000).toFixed(2)}M`
  } else if (digits > 3) {
    return `${sign}${symbol}${(value / 1_000).toFixed(1)}K`
  }

  return formatMoney(amount, currency)
}

/** A rating, e.g. "4.80" -> "4.8". */
export function formatRating(rating: Money | null | undefined): string {
  if (!rating) return '—'
  const value = Number(rating)
  return Number.isFinite(value) ? value.toFixed(1) : '—'
}

/**
 * Trim trailing decimal zeros for an EDITABLE field: "5.00" -> "5".
 *
 * The server stores DECIMAL, so a value round-trips as "5.00". Showing that in
 * a number input invites the reader to wonder what the decimals signify, and
 * some browsers reformat it on focus. Display-only money keeps its decimals —
 * this is for inputs.
 */
export function trimDecimals(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  if (!value.includes('.')) return value
  return value.replace(/0+$/, '').replace(/\.$/, '') || '0'
}

/**
 * A percentage as a human would write it: "5.0000" -> "5%", "18.50" -> "18.5%".
 *
 * The server stores DECIMAL(9,4) for precision, and rendering that raw gives
 * "5.0000%" — technically right and visibly wrong. Trailing zeros are trimmed
 * without touching significant digits.
 */
export function formatPercent(value: Money | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'

  // Trim in the STRING, not via parseFloat — the same precision reasoning as
  // money, and it keeps "0.0500" from becoming "0.05000000000000001".
  const trimmed = value.includes('.')
    ? value.replace(/0+$/, '').replace(/\.$/, '')
    : value

  return `${trimmed || '0'}%`
}

// -------------------------------------------------------------------- dates
/**
 * *** ALWAYS PARSE THROUGH Date, NEVER SLICE THE STRING ***
 * The server sends UTC with a `Z`. Slicing "2026-09-20T04:30:00Z" to get a time
 * would display 04:30 to an admin in Kolkata, where the session is at 10:00.
 */
function toDate(value: UtcTimestamp | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "20 Sep 2026, 10:00 am" in the VIEWER'S timezone. */
export function formatDateTime(value: UtcTimestamp | null | undefined): string {
  const date = toDate(value)
  if (!date) return '—'

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

/** "20 Sep 2026" — no time component. */
export function formatDate(value: UtcTimestamp | null | undefined): string {
  const date = toDate(value)
  if (!date) return '—'

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** "10:00 am". */
export function formatTime(value: UtcTimestamp | null | undefined): string {
  const date = toDate(value)
  if (!date) return '—'

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

/**
 * Does an instant show a DIFFERENT wall-clock time in the given zone than it
 * does for the viewer?
 *
 * Used to decide whether showing the mentor's local time tells the reader
 * anything. Comparing zone NAMES is not sufficient: "Asia/Calcutta" and
 * "Asia/Kolkata" are the same offset under different names, and an admin
 * sitting in the mentor's own zone would otherwise see the identical time
 * printed twice as if it meant something.
 *
 * Comparing the actual rendered clock time answers the real question.
 */
export function differsInTimezone(
  value: UtcTimestamp | null | undefined,
  timeZone: string,
): boolean {
  const date = toDate(value)
  if (!date) return false

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }

  try {
    const local = new Intl.DateTimeFormat(undefined, options).format(date)
    const there = new Intl.DateTimeFormat(undefined, {
      ...options,
      timeZone,
    }).format(date)
    return local !== there
  } catch {
    // An unrecognised zone tells us nothing useful, so say nothing.
    return false
  }
}

/**
 * The same instant in a specific IANA zone — used to show a session in the
 * MENTOR'S timezone next to the admin's own.
 *
 * A support call is usually "the mentor says the time is wrong", and seeing
 * both at once answers it immediately.
 */
export function formatInTimezone(
  value: UtcTimestamp | null | undefined,
  timeZone: string,
): string {
  const date = toDate(value)
  if (!date) return '—'

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    }).format(date)
  } catch {
    // An unknown zone must not blank the cell.
    return formatDateTime(value)
  }
}

/** "2 hours ago", "in 3 days". */
export function formatRelative(value: UtcTimestamp | null | undefined): string {
  const date = toDate(value)
  if (!date) return '—'

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / 60_000)
  const absMinutes = Math.abs(diffMinutes)

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (absMinutes < 1) return 'just now'
  if (absMinutes < 60) return rtf.format(diffMinutes, 'minute')
  if (absMinutes < 1440) return rtf.format(Math.round(diffMinutes / 60), 'hour')
  if (absMinutes < 43_200) return rtf.format(Math.round(diffMinutes / 1440), 'day')
  return rtf.format(Math.round(diffMinutes / 43_200), 'month')
}

/** `YYYY-MM-DD` for a date input. Uses LOCAL parts, not UTC ones. */
export function toDateInputValue(value: UtcTimestamp | null | undefined): string {
  const date = toDate(value)
  if (!date) return ''

  // `toISOString()` would shift the day for anyone east or west of UTC around
  // midnight — a date picker showing the wrong day is a real bug.
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ------------------------------------------------------------------- misc
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(
    Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)),
    units.length - 1,
  )
  const value = bytes / 1024 ** exponent
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat().format(value)
}

/** "1 h 30 m" from minutes. */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '—'
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} m` : `${hours} h`
}

/** Turn an enum value into a readable label: PENDING_PAYMENT -> Pending payment. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—'
  const spaced = value.replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?'
}
